/**
 * Per-connection MCP data grants stored on api_keys.mcp_grants.
 * Empty / missing allowlists mean "all items in that category".
 */

export const MCP_CATEGORIES = [
  "documents",
  "threads",
  "spaces",
  "workspaces",
  "topics",
  "communications",
  "github",
] as const;

export type McpCategory = (typeof MCP_CATEGORIES)[number];

export const MCP_COMMUNICATION_SOURCES = [
  "gmail",
  "fireflies",
  "clickup",
  "telegram",
  "gohighlevel",
  "zoom",
  "ringcentral",
  "google_calendar",
] as const;

export type McpCommunicationSource = (typeof MCP_COMMUNICATION_SOURCES)[number];

export type McpAllowlists = {
  documents?: string[] | null;
  threads?: string[] | null;
  spaces?: string[] | null;
  workspaces?: string[] | null;
  topics?: string[] | null;
  communications?: McpCommunicationSource[] | null;
  /** GitHub repos as `owner/repo`. */
  github?: string[] | null;
};

export type McpLenses = {
  sourceOfTruth?: boolean;
  personalDna?: boolean;
  faith?: boolean;
  gcc?: boolean;
};

/**
 * Write actions an MCP connection may perform. Unlike read categories, these
 * default to OFF when missing, so existing keys and OAuth connectors stay
 * read-only until the owner opts in.
 */
export type McpWrites = {
  clickupCreateTask?: boolean;
  /** Create Gmail drafts (nothing is sent). */
  gmailDraft?: boolean;
  /** Send a draft created by gmail_create_draft, and email calendar invitations. */
  gmailSend?: boolean;
  /** Create Google Calendar events. */
  calendarCreateEvent?: boolean;
};

export const MCP_WRITE_KEYS = ["clickupCreateTask", "gmailDraft", "gmailSend", "calendarCreateEvent"] as const;

/**
 * Live reads from a connected third-party account (as opposed to the indexed
 * `communications` category). Default OFF when missing: an existing key or OAuth
 * connector must not start reading a live mailbox or calendar by itself.
 * Needs the server-side Google connection (docs/GOOGLE-SERVER-CONNECTION-PHASE1-PLAN.md).
 */
export type McpReads = {
  gmailRead?: boolean;
  calendarRead?: boolean;
};

export const MCP_READ_KEYS = ["gmailRead", "calendarRead"] as const;

export type McpGrants = {
  categories: Record<McpCategory, boolean>;
  allowlists?: McpAllowlists;
  /** Retrieval filters applied by librarian_ask (default: all on). */
  lenses?: McpLenses;
  /** Opt-in write actions (default: all off). */
  writes?: McpWrites;
  /** Opt-in live reads of connected accounts (default: all off). */
  reads?: McpReads;
};

const MAX_ALLOWLIST = 2000;

export const DEFAULT_MCP_GRANTS: McpGrants = {
  categories: {
    documents: true,
    threads: true,
    spaces: true,
    workspaces: true,
    topics: true,
    communications: true,
    github: true,
  },
  lenses: {
    sourceOfTruth: true,
    personalDna: true,
    faith: true,
    gcc: true,
  },
  writes: { clickupCreateTask: false, gmailDraft: false, gmailSend: false, calendarCreateEvent: false },
  reads: { gmailRead: false, calendarRead: false },
};

/** Used when stored jsonb is invalid — fail closed. */
export const DENY_ALL_MCP_GRANTS: McpGrants = {
  categories: {
    documents: false,
    threads: false,
    spaces: false,
    workspaces: false,
    topics: false,
    communications: false,
    github: false,
  },
  lenses: {
    sourceOfTruth: false,
    personalDna: false,
    faith: false,
    gcc: false,
  },
  writes: { clickupCreateTask: false, gmailDraft: false, gmailSend: false, calendarCreateEvent: false },
  reads: { gmailRead: false, calendarRead: false },
};

function cloneDefault(): McpGrants {
  return {
    categories: { ...DEFAULT_MCP_GRANTS.categories },
    allowlists: {},
    lenses: { ...DEFAULT_MCP_GRANTS.lenses },
    writes: { ...DEFAULT_MCP_GRANTS.writes },
    reads: { ...DEFAULT_MCP_GRANTS.reads },
  };
}

function isCommunicationSource(value: string): value is McpCommunicationSource {
  return (MCP_COMMUNICATION_SOURCES as readonly string[]).includes(value);
}

function parseIdList(input: unknown, label: string): { ids?: string[] | null; error?: string } {
  if (input === undefined || input === null) return { ids: null };
  if (!Array.isArray(input)) {
    return { error: `"allowlists.${label}" must be an array of strings or null` };
  }
  if (input.length > MAX_ALLOWLIST) {
    return { error: `"allowlists.${label}" may contain at most ${MAX_ALLOWLIST} ids` };
  }
  if (!input.every((s) => typeof s === "string" && s.trim().length > 0)) {
    return { error: `"allowlists.${label}" must contain non-empty strings` };
  }
  return { ids: input.map((s: string) => s.trim()) };
}

/**
 * Normalize unknown JSON into McpGrants. Missing categories default to on
 * (same as today's whole-account MCP access). Invalid shape returns an error.
 */
export function parseMcpGrants(input: unknown): { grants?: McpGrants; error?: string } {
  if (input === undefined || input === null) {
    return { grants: cloneDefault() };
  }
  if (typeof input !== "object" || Array.isArray(input)) {
    return { error: '"mcpGrants" must be an object' };
  }
  const raw = input as Record<string, unknown>;
  const grants = cloneDefault();

  if (raw.categories !== undefined) {
    if (!raw.categories || typeof raw.categories !== "object" || Array.isArray(raw.categories)) {
      return { error: '"mcpGrants.categories" must be an object' };
    }
    const cats = raw.categories as Record<string, unknown>;
    for (const key of Object.keys(cats)) {
      if (!(MCP_CATEGORIES as readonly string[]).includes(key)) {
        return { error: `Unknown MCP category "${key}"` };
      }
      if (typeof cats[key] !== "boolean") {
        return { error: `"mcpGrants.categories.${key}" must be a boolean` };
      }
      grants.categories[key as McpCategory] = cats[key] as boolean;
    }
  }

  if (raw.allowlists !== undefined && raw.allowlists !== null) {
    if (typeof raw.allowlists !== "object" || Array.isArray(raw.allowlists)) {
      return { error: '"mcpGrants.allowlists" must be an object' };
    }
    const lists = raw.allowlists as Record<string, unknown>;
    for (const key of Object.keys(lists)) {
      if (!(MCP_CATEGORIES as readonly string[]).includes(key)) {
        return { error: `Unknown MCP allowlist "${key}"` };
      }
    }

    const itemCategories = ["documents", "threads", "spaces", "workspaces", "topics", "github"] as const;
    for (const key of itemCategories) {
      if (!(key in lists)) continue;
      const parsed = parseIdList(lists[key], key);
      if (parsed.error) return { error: parsed.error };
      grants.allowlists![key] = parsed.ids ?? null;
    }

    if ("communications" in lists) {
      const comm = lists.communications;
      if (comm === undefined || comm === null) {
        grants.allowlists!.communications = null;
      } else if (!Array.isArray(comm) || !comm.every((s) => typeof s === "string")) {
        return { error: '"allowlists.communications" must be an array of platform ids or null' };
      } else {
        for (const src of comm) {
          if (!isCommunicationSource(src)) {
            return {
              error: `Invalid communications platform "${src}". Use: ${MCP_COMMUNICATION_SOURCES.join(", ")}`,
            };
          }
        }
        grants.allowlists!.communications = comm as McpCommunicationSource[];
      }
    }
  }

  if (raw.lenses !== undefined && raw.lenses !== null) {
    if (typeof raw.lenses !== "object" || Array.isArray(raw.lenses)) {
      return { error: '"mcpGrants.lenses" must be an object' };
    }
    const lenses = raw.lenses as Record<string, unknown>;
    const keys = ["sourceOfTruth", "personalDna", "faith", "gcc"] as const;
    grants.lenses = { ...DEFAULT_MCP_GRANTS.lenses };
    for (const key of Object.keys(lenses)) {
      if (!(keys as readonly string[]).includes(key)) {
        return { error: `Unknown MCP lens "${key}"` };
      }
      if (typeof lenses[key] !== "boolean") {
        return { error: `"mcpGrants.lenses.${key}" must be a boolean` };
      }
      grants.lenses[key as keyof McpLenses] = lenses[key] as boolean;
    }
  }

  if (raw.writes !== undefined && raw.writes !== null) {
    if (typeof raw.writes !== "object" || Array.isArray(raw.writes)) {
      return { error: '"mcpGrants.writes" must be an object' };
    }
    const writes = raw.writes as Record<string, unknown>;
    grants.writes = { ...DEFAULT_MCP_GRANTS.writes };
    for (const key of Object.keys(writes)) {
      if (!(MCP_WRITE_KEYS as readonly string[]).includes(key)) {
        return { error: `Unknown MCP write action "${key}"` };
      }
      if (typeof writes[key] !== "boolean") {
        return { error: `"mcpGrants.writes.${key}" must be a boolean` };
      }
      grants.writes[key as keyof McpWrites] = writes[key] as boolean;
    }
  }

  if (raw.reads !== undefined && raw.reads !== null) {
    if (typeof raw.reads !== "object" || Array.isArray(raw.reads)) {
      return { error: '"mcpGrants.reads" must be an object' };
    }
    const reads = raw.reads as Record<string, unknown>;
    grants.reads = { ...DEFAULT_MCP_GRANTS.reads };
    for (const key of Object.keys(reads)) {
      if (!(MCP_READ_KEYS as readonly string[]).includes(key)) {
        return { error: `Unknown MCP live-read "${key}"` };
      }
      if (typeof reads[key] !== "boolean") {
        return { error: `"mcpGrants.reads.${key}" must be a boolean` };
      }
      grants.reads[key as keyof McpReads] = reads[key] as boolean;
    }
  }

  return { grants };
}

export function readEnabled(grants: McpGrants, key: keyof McpReads): boolean {
  return grants.reads?.[key] === true;
}

export function writeEnabled(grants: McpGrants, key: keyof McpWrites): boolean {
  return grants.writes?.[key] === true;
}

export function categoryEnabled(grants: McpGrants, category: McpCategory): boolean {
  return grants.categories[category] === true;
}

/**
 * Item-level check. `id` is a document/thread/space/workspace/topic id, or a
 * communications platform slug. Empty allowlist array means nothing is allowed.
 */
export function isAllowed(grants: McpGrants, category: McpCategory, id: string): boolean {
  if (!categoryEnabled(grants, category)) return false;
  const list = grants.allowlists?.[category];
  if (list === undefined || list === null) return true;
  return list.includes(id as never);
}

export function allowedCommunicationSources(grants: McpGrants): McpCommunicationSource[] | null {
  if (!categoryEnabled(grants, "communications")) return [];
  const list = grants.allowlists?.communications;
  if (list === undefined || list === null) return null;
  return list;
}

export function allowedGithubRepos(grants: McpGrants): string[] | null {
  if (!categoryEnabled(grants, "github")) return [];
  const list = grants.allowlists?.github;
  if (list === undefined || list === null) return null;
  return list;
}

export function lensEnabled(grants: McpGrants, lens: keyof McpLenses): boolean {
  if (grants.lenses?.[lens] === false) return false;
  return true;
}

export function cloneMcpGrants(grants?: McpGrants | null): McpGrants {
  const src = grants ?? DEFAULT_MCP_GRANTS;
  return {
    categories: { ...DEFAULT_MCP_GRANTS.categories, ...src.categories },
    allowlists: {
      documents: copyList(src.allowlists?.documents),
      threads: copyList(src.allowlists?.threads),
      spaces: copyList(src.allowlists?.spaces),
      workspaces: copyList(src.allowlists?.workspaces),
      topics: copyList(src.allowlists?.topics),
      communications: copyList(src.allowlists?.communications),
      github: copyList(src.allowlists?.github),
    },
    lenses: { ...DEFAULT_MCP_GRANTS.lenses, ...src.lenses },
    writes: { ...DEFAULT_MCP_GRANTS.writes, ...src.writes },
    reads: { ...DEFAULT_MCP_GRANTS.reads, ...src.reads },
  };
}

function copyList<T extends string>(list: T[] | null | undefined): T[] | null | undefined {
  if (list === undefined) return undefined;
  if (list === null) return null;
  return [...list];
}

/**
 * Narrow `base` by `narrow` — never widens. A category or lens stays on only
 * when both allow it; allowlists intersect (missing/null = no restriction, so
 * the other side's list applies; two lists keep only the ids in both).
 */
export function intersectMcpGrants(base: McpGrants, narrow: McpGrants): McpGrants {
  const out = cloneMcpGrants(base);
  for (const c of MCP_CATEGORIES) {
    out.categories[c] = base.categories[c] === true && narrow.categories[c] === true;
    const a = base.allowlists?.[c];
    const b = narrow.allowlists?.[c];
    if (b === undefined || b === null) continue;
    const next = a === undefined || a === null ? [...b] : (a as string[]).filter((id) => (b as string[]).includes(id));
    (out.allowlists as Record<McpCategory, string[] | null | undefined>)[c] = next;
  }
  const lenses: (keyof McpLenses)[] = ["sourceOfTruth", "personalDna", "faith", "gcc"];
  out.lenses = Object.fromEntries(
    lenses.map((k) => [k, lensEnabled(base, k) && lensEnabled(narrow, k)])
  ) as McpLenses;
  return out;
}

/**
 * The grants a REST request runs under.
 *  - API key (`dsk_…`): the key's stored grants (fail closed when missing).
 *    A `mcpGrants` body may only NARROW them — it can never widen a key.
 *  - Session (the signed-in web app): the body's grants when given, else the
 *    account default (unchanged behaviour).
 * An invalid body returns an error rather than being silently ignored.
 */
export function grantsForRequest(
  auth: { via: "api_key" | "session"; mcpGrants?: McpGrants },
  bodyGrants: unknown
): { grants?: McpGrants; error?: string } {
  const hasBody = bodyGrants !== undefined && bodyGrants !== null;
  const parsed = hasBody ? parseMcpGrants(bodyGrants) : null;
  if (parsed?.error) return { error: parsed.error };
  if (auth.via === "api_key") {
    const base = auth.mcpGrants ?? DENY_ALL_MCP_GRANTS;
    return { grants: parsed?.grants ? intersectMcpGrants(base, parsed.grants) : cloneMcpGrants(base) };
  }
  return { grants: parsed?.grants ?? auth.mcpGrants ?? cloneMcpGrants(DEFAULT_MCP_GRANTS) };
}

export function summarizeMcpGrants(grants: McpGrants): string {
  const on = MCP_CATEGORIES.filter((c) => grants.categories[c]);
  if (on.length === 0) return "Nothing shared";
  if (on.length === MCP_CATEGORIES.length) {
    const restricted = MCP_CATEGORIES.filter((c) => {
      const list = grants.allowlists?.[c];
      return Array.isArray(list);
    });
    if (restricted.length === 0) return "All data";
  }
  const labels: Record<McpCategory, string> = {
    documents: "Documents",
    threads: "Threads",
    spaces: "Spaces",
    workspaces: "Workspaces",
    topics: "Topics",
    communications: "Communications",
    github: "GitHub",
  };
  return on
    .map((c) => {
      const list = grants.allowlists?.[c];
      if (Array.isArray(list)) return `${labels[c]} (${list.length})`;
      return labels[c];
    })
    .join(", ");
}
